/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

window.changeUpsellQty = function(button, amount) {
  const input = button?.parentElement?.querySelector('.qty-input');

  if (!input) {
    return;
  }

  const currentValue = Number.parseInt(input.value, 10) || 1;
  input.value = Math.max(1, currentValue + amount);
};

window.addUpsellToCart = async function(button, variantId) {
  const container = button?.closest('.upsell-block');
  const quantityInput = container?.querySelector('.qty-input');

  if (!button || !container || !quantityInput || button.getAttribute('aria-busy') === 'true') {
    return;
  }

  const quantity = Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);
  const originalText = button.textContent;
  const cartAddUrl = window.themeVariables?.routes?.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

  button.setAttribute('aria-live', 'polite');
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  button.textContent = 'Ajout en cours…';

  try {
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Ajout impossible');
    }

    button.textContent = 'Ajouté ✓';
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  } catch (error) {
    button.textContent = error.message || 'Erreur de stock';
    button.classList.add('upsell-button--error');
  } finally {
    window.setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('upsell-button--error');
    }, 2200);
  }
};

document.addEventListener('click', (event) => {
  const quantityButton = event.target.closest('[data-upsell-quantity]');

  if (quantityButton) {
    window.changeUpsellQty(quantityButton, Number(quantityButton.dataset.upsellQuantity));
    return;
  }

  const addButton = event.target.closest('[data-upsell-variant-id]');

  if (addButton) {
    window.addUpsellToCart(addButton, addButton.dataset.upsellVariantId);
  }
});

class KonotoreeBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.bundleItems = [...this.querySelectorAll('[data-bundle-item]')];
    this.addButton = this.querySelector('[data-bundle-add]');
    this.status = this.querySelector('[data-bundle-status]');
    this.total = this.querySelector('[data-bundle-total]');
    this.buttonLabel = this.querySelector('[data-bundle-button-label]');
    this.mainPriceLabel = this.querySelector('[data-main-price-label]');
    this.context = this.dataset.context;
    this.mainPrice = Number(this.dataset.mainPrice || 0);
    this.mainVariantId = Number(this.dataset.mainVariantId || 0);
    this.mainAvailable = this.dataset.mainAvailable !== 'false';

    this.addEventListener('click', this.handleClick.bind(this));
    this.bundleItems.forEach((item) => {
      item.querySelector('[data-bundle-quantity-input]')?.addEventListener('input', () => this.update());
      item.querySelector('[data-bundle-toggle]')?.addEventListener('change', () => this.update());
    });
    this.addButton?.addEventListener('click', () => this.addToCart());

    if (this.context === 'product') {
      document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
    }

    this.update();
  }

  handleClick(event) {
    const tierButton = event.target.closest('[data-bundle-tier]');
    if (tierButton && this.contains(tierButton)) {
      const item = tierButton.closest('[data-bundle-item]');
      const input = item?.querySelector('[data-bundle-quantity-input]');
      const toggle = item?.querySelector('[data-bundle-toggle]');
      if (!input || !toggle) return;
      input.value = Math.max(1, Number(tierButton.dataset.bundleTier || 1));
      toggle.checked = true;
      this.update();
      return;
    }

    const quantityButton = event.target.closest('[data-bundle-quantity]');
    if (!quantityButton || !this.contains(quantityButton)) return;

    const item = quantityButton.closest('[data-bundle-item]');
    const input = item?.querySelector('[data-bundle-quantity-input]');
    if (!input) return;

    const delta = Number(quantityButton.dataset.bundleQuantity || 0);
    const current = this.getQuantity(item);
    input.value = Math.max(1, current + delta);
    this.update();
  }

  handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    this.mainVariantId = Number(variant.id);
    this.mainPrice = Number(variant.price || 0);
    this.mainAvailable = Boolean(variant.available);
    this.dataset.mainVariantId = String(this.mainVariantId);
    this.dataset.mainPrice = String(this.mainPrice);
    this.dataset.mainAvailable = String(this.mainAvailable);
    this.update();
  }

  getQuantity(item) {
    const input = item?.querySelector('[data-bundle-quantity-input]');
    return Math.max(1, Number.parseInt(input?.value, 10) || 1);
  }

  getUnitPrice(item, quantity) {
    const basePrice = Number(item?.dataset.unitPrice || 0);
    if (item?.dataset.singleBundle === 'true') return Number(item.dataset.bundlePrice || basePrice);
    if (item?.dataset.volumePricing !== 'true') return basePrice;
    if (quantity >= 10) return Number(item.dataset.price10 || basePrice);
    if (quantity >= 5) return Number(item.dataset.price5 || basePrice);
    return Number(item.dataset.bundlePrice || basePrice);
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || window.themeVariables?.settings?.moneyFormat || '{{ amount }}';
    const number = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    return format.replace(/\{\{\s*amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?\s*\}\}/, number);
  }

  update() {
    let selectedPrice = 0;
    let selectedCount = 0;

    this.bundleItems.forEach((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      const quantityInput = item.querySelector('[data-bundle-quantity-input]');
      const quantitySelector = item.querySelector('.konotoree-bundle__quantity');
      const stateLabel = item.querySelector('[data-bundle-state-label]');
      const quantity = this.getQuantity(item);
      const unitPrice = this.getUnitPrice(item, quantity);
      const selected = Boolean(toggle?.checked);

      if (quantityInput) quantityInput.value = quantity;
      item.classList.toggle('is-selected', selected);
      if (quantitySelector) quantitySelector.hidden = !selected || item.dataset.volumePricing === 'true' || item.dataset.singleBundle === 'true';
      if (stateLabel) {
        stateLabel.textContent = item.dataset.singleBundle === 'true'
          ? (selected ? 'Bundle ajouté ✓' : '+ Ajouter le bundle')
          : (selected ? 'Ajouté ✓' : '+ Ajouter');
      }

      const unitPriceLabel = item.querySelector('[data-bundle-unit-price]');
      if (unitPriceLabel) {
        unitPriceLabel.textContent = item.dataset.singleBundle === 'true'
          ? `${this.formatMoney(unitPrice)} avec le magazine au panier`
          : `${this.formatMoney(unitPrice)}/u${quantity < 5 && item.dataset.bundlePrice ? ' avec le magazine au panier' : ''}`;
      }

      const activeTier = quantity >= 10 ? 10 : quantity >= 5 ? 5 : 1;
      item.querySelectorAll('[data-bundle-tier]').forEach((button) => {
        const isActive = selected && Number(button.dataset.bundleTier) === activeTier;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      const tierSummary = item.querySelector('[data-bundle-tier-summary]');
      if (tierSummary) {
        const total = unitPrice * quantity;
        const savings = Math.max(0, (Number(item.dataset.unitPrice || 0) - unitPrice) * quantity);
        tierSummary.textContent = selected
          ? `${quantity} × ${this.formatMoney(unitPrice)} = ${this.formatMoney(total)}${savings > 0 ? ` · ${this.formatMoney(savings)} économisés` : ''}`
          : item.dataset.bundlePrice
            ? `Avec le magazine au panier : ${this.formatMoney(Number(item.dataset.bundlePrice))}/u · remise automatique`
            : 'Choisissez 1, 5 ou 10 · remise automatique au panier';
      }

      if (selected) {
        selectedCount += 1;
        selectedPrice += unitPrice * quantity;
      }
    });

    const totalPrice = selectedPrice;
    if (this.total) this.total.textContent = this.formatMoney(totalPrice);

    if (this.buttonLabel) {
      this.buttonLabel.textContent = selectedCount > 0 ? 'Ajouter la sélection' : 'Choisir une protection';
    }

    if (this.addButton) {
      this.addButton.disabled = selectedCount === 0;
    }
  }

  async addToCart() {
    if (!this.addButton || this.addButton.getAttribute('aria-busy') === 'true') return;

    const items = [];

    this.bundleItems.forEach((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      const variantId = Number(item.dataset.variantId || 0);

      if (toggle?.checked && variantId) {
        items.push({ id: variantId, quantity: this.getQuantity(item) });
      }
    });

    if (items.length === 0) return;

    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.disabled = true;
    if (this.buttonLabel) this.buttonLabel.textContent = 'Ajout en cours…';
    this.showStatus('');

    try {
      const response = await fetch(window.themeVariables?.routes?.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: ['mini-cart'],
          sections_url: window.location.pathname
        })
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.description || responseData.message || 'Ajout impossible.');
      }

      if (this.buttonLabel) this.buttonLabel.textContent = 'Ajouté ✓';
      this.showStatus('Sélection ajoutée au panier.');

      const cartResponse = await fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`, {
        headers: { Accept: 'application/json' }
      });
      const cartContent = await cartResponse.json();

      if (responseData.sections) {
        cartContent.sections = responseData.sections;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cartContent }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: window.themeVariables?.settings?.cartType === 'drawer' && !this.closest('.drawer')
        }
      }));

      if (this.context === 'cart' && window.themeVariables?.settings?.pageType === 'cart') {
        window.setTimeout(() => window.location.reload(), 450);
      }
    } catch (error) {
      if (this.buttonLabel) this.buttonLabel.textContent = 'Réessayer';
      this.showStatus(error.message || 'Une erreur est survenue.', true);
    } finally {
      window.setTimeout(() => {
        if (this.isConnected && this.addButton) {
          this.addButton.removeAttribute('aria-busy');
          this.update();
        }
      }, 2200);
    }
  }

  showStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('is-error', isError);
  }
}

if (!window.customElements.get('konotoree-bundle')) {
  window.customElements.define('konotoree-bundle', KonotoreeBundle);
}

class KonotoreeVolumePicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.formId = this.dataset.formId;
    this.quantityInput = document.querySelector(`input[name="quantity"][form="${CSS.escape(this.formId)}"]`);
    this.buttons = [...this.querySelectorAll('[data-volume-quantity]')];
    this.customInput = this.querySelector('[data-volume-custom-input]');
    this.adjustButtons = [...this.querySelectorAll('[data-volume-adjust]')];
    this.quantityLabel = this.querySelector('[data-volume-summary-quantity]');
    this.unitLabel = this.querySelector('[data-volume-summary-unit]');
    this.totalLabel = this.querySelector('[data-volume-summary-total]');
    this.savingLabel = this.querySelector('[data-volume-summary-saving]');
    this.buttons.forEach((button) => button.addEventListener('click', () => this.selectQuantity(Number(button.dataset.volumeQuantity || 1))));
    this.adjustButtons.forEach((button) => button.addEventListener('click', () => {
      this.selectQuantity(this.getQuantity() + Number(button.dataset.volumeAdjust || 0));
    }));
    this.customInput?.addEventListener('input', () => {
      const quantity = Math.max(1, Number.parseInt(this.customInput.value, 10) || 1);
      if (this.quantityInput) {
        this.quantityInput.value = quantity;
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.update();
    });
    this.customInput?.addEventListener('change', () => this.selectQuantity(Math.max(1, Number.parseInt(this.customInput.value, 10) || 1)));
    this.quantityInput?.addEventListener('input', () => this.update());
    this.quantityInput?.addEventListener('change', () => this.update());
    this.update();
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || window.themeVariables?.settings?.moneyFormat || '{{ amount }}';
    const number = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    return format.replace(/\{\{\s*amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator|_with_apostrophe_separator)?\s*\}\}/, number);
  }

  getQuantity() {
    return Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);
  }

  getUnitPrice(quantity) {
    const basePrice = Number(this.dataset.basePrice || 0);
    if (quantity >= 10) return Number(this.dataset.price10 || basePrice);
    if (quantity >= 5) return Number(this.dataset.price5 || basePrice);
    return basePrice;
  }

  selectQuantity(quantity) {
    if (!this.quantityInput) return;
    this.quantityInput.value = quantity;
    this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.update();
  }

  update() {
    const quantity = this.getQuantity();
    const unitPrice = this.getUnitPrice(quantity);
    const basePrice = Number(this.dataset.basePrice || 0);
    const total = unitPrice * quantity;
    const savings = Math.max(0, (basePrice - unitPrice) * quantity);
    const activeTier = quantity >= 10 ? 10 : quantity >= 5 ? 5 : 1;
    if (this.customInput) this.customInput.value = quantity;
    this.buttons.forEach((button) => {
      const isActive = Number(button.dataset.volumeQuantity) === activeTier;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    if (this.quantityLabel) this.quantityLabel.textContent = `${quantity} protection${quantity > 1 ? 's' : ''}`;
    if (this.unitLabel) this.unitLabel.textContent = `${this.formatMoney(unitPrice)}/unité`;
    if (this.totalLabel) this.totalLabel.textContent = this.formatMoney(total);
    if (this.savingLabel) {
      this.savingLabel.hidden = savings === 0;
      this.savingLabel.textContent = savings > 0 ? `· ${this.formatMoney(savings)} économisés` : '';
    }
  }
}

if (!window.customElements.get('konotoree-volume-picker')) {
  window.customElements.define('konotoree-volume-picker', KonotoreeVolumePicker);
}
